import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MockCard, MockHeader } from './shell';
import { EASE } from '../brand';

const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const X = [34, 88, 143, 197, 252, 306];

// % of Gold Medal Standard, plotted on an 82–92% axis.
const SERIES = [
  {
    key: 'squad',
    label: 'Squad average',
    color: '#94a3b8',
    y: [91.8, 83.2, 71.7, 64, 50.6, 43.8],
  },
  {
    key: 'athlete',
    label: 'J. Okafor',
    color: '#2563eb',
    y: [68.8, 56.3, 53.4, 33.3, 29.4, 23.7],
  },
];

const toPath = (ys) => ys.map((y, i) => `${i === 0 ? 'M' : 'L'}${X[i]} ${y}`).join(' ');

const GRID = [
  { y: 16, label: '92%' },
  { y: 45, label: '89%' },
  { y: 74, label: '86%' },
  { y: 102, label: '83%' },
];

export default function AnalyticsMockup() {
  const reduce = useReducedMotion();
  const athlete = SERIES[1];
  const areaPath = `${toPath(athlete.y)} L306 120 L34 120 Z`;

  return (
    <MockCard>
      <MockHeader
        title="Team analytics"
        subtitle={'% of Gold Medal Standard \u00b7 Men\u2019s Varsity'}
        right={
          <span className="shrink-0 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
            Export CSV
          </span>
        }
      />

      <div className="px-3 pb-2 pt-4">
        <svg viewBox="0 0 320 140" className="w-full" role="img" aria-label="Progression chart">
          <defs>
            <linearGradient id="rc-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
            </linearGradient>
          </defs>

          {GRID.map((g) => (
            <g key={g.y}>
              <line x1="34" y1={g.y} x2="306" y2={g.y} stroke="#e2e8f0" strokeWidth="1" />
              <text x="28" y={g.y + 3} textAnchor="end" fontSize="8" fill="#94a3b8">
                {g.label}
              </text>
            </g>
          ))}

          <motion.path
            d={areaPath}
            fill="url(#rc-area)"
            initial={reduce ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, delay: 0.6 }}
          />

          {SERIES.map((s, si) => (
            <motion.path
              key={s.key}
              d={toPath(s.y)}
              fill="none"
              stroke={s.color}
              strokeWidth={s.key === 'athlete' ? 2.4 : 1.6}
              strokeDasharray={s.key === 'squad' ? '4 4' : undefined}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? false : { pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 1.3, delay: 0.15 + si * 0.15, ease: EASE }}
            />
          ))}

          {athlete.y.map((y, i) => (
            <motion.circle
              key={X[i]}
              cx={X[i]}
              cy={y}
              r="3"
              fill="#ffffff"
              stroke="#2563eb"
              strokeWidth="2"
              initial={reduce ? false : { opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.3, delay: 0.5 + i * 0.1, ease: EASE }}
            />
          ))}

          {MONTHS.map((m, i) => (
            <text key={m} x={X[i]} y="134" textAnchor="middle" fontSize="8" fill="#94a3b8">
              {m}
            </text>
          ))}
        </svg>
      </div>

      <div className="flex items-center gap-4 px-4 pb-3 text-[11px] text-slate-500">
        {SERIES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-px border-t border-slate-100 bg-slate-100">
        {[
          ['+4.7 pts', 'GMS since Oct'],
          ['18', 'athletes tracked'],
          ['94%', 'plan compliance'],
        ].map(([value, label]) => (
          <div key={label} className="bg-white px-2 py-2.5 text-center">
            <p className="text-sm font-bold text-slate-900">{value}</p>
            <p className="text-[10px] text-slate-500">{label}</p>
          </div>
        ))}
      </div>
    </MockCard>
  );
}
